import AxiosInstance from "./AxiosInstance"
import * as utils from "../helpers/utils"
import {
    IAccountRegistraionDTO, EPlanType, EPunchSetApprovalType, ERecordSearchFilter, IAccrualAdjustment,
    IAccrualSettings, IPunchExceptionFilterDto, IUserFilter, IPosition, IJobFilter, IFilterSupervisorDTO, IPayrollPayGroupData, IScheduleTemplate, EExceptionType, ITimeoffFilter
} from "../models"
import AccountStore from "../stores/AccountStore"
import AuthenticationStore from "../stores/AuthenticationStore"

let axiosInstance = new AxiosInstance().instance
axiosInstance.interceptors.request.use(config => {
    if (AccountStore.getCurrentAccount()) {
        const proxy = localStorage.getItem("findd_proxy")
        if (proxy !== null) {
            if (AccountStore.getProxyAccountId() && proxy !== AccountStore.getProxyAccountId()) {
                console.log(`AccountStore.getProxyAccountId(): ${AccountStore.getProxyAccountId()}, proxy: ${proxy}`)
                //Don't allow any further API calls. We are in a bad state.
                axiosInstance = null
                AuthenticationStore.setCrossProxyError()
            }
        }
        config.headers["TicksSinceChange"] = AccountStore.getCurrentAccount().TicksSinceChange
        config.headers["proxy"] = proxy
    }
    return config
})

export function getAccount(accountId) {
    return axiosInstance.get("/accounts/" + accountId)
}

export function getUIUsers(
    accountId: string,
    page: number,
    limit: number,
    filterText: string,
    levelIdFilter: string = "",
    activeUsers: boolean = true,
    filterByDirectReports: boolean = false,
    filterByPunchDefault: boolean = false,
    jobId: string = ""
) {
    const limitOnAPI = limit === -1 ? null : limit
    return axiosInstance.get(
        "/accounts/" +
        accountId +
        "/uiusers?" +
        "levelIdFilter=" +
        levelIdFilter +
        "&page=" +
        page +
        "&limit=" +
        limitOnAPI +
        "&search=" +
        filterText +
        "&activeUsers=" +
        activeUsers +
        "&filterByDirectReports=" +
        filterByDirectReports +
        "&filterByPunchDefault=" +
        filterByPunchDefault +
        "&jobId=" +
        jobId
    )
}

export function getLinkedEmployees(accountId, hierarchyItemId, page, limit, search, activeUsers = true) {
    return axiosInstance.get(
        "/accounts/" + accountId + "/hierarchy_items/" + hierarchyItemId + "/linked_employees?page=" + page + "&limit=" + limit + "&search=" + search + "&activeUsers=" + activeUsers
    )
}

export function getI9Users(accountId, page, limit, filterText) {
    return axiosInstance.get("/accounts/" + accountId + "/i9_users?page=" + page + "&limit=" + limit + "&search=" + filterText)
}

export function getClientIP() {
    return axiosInstance.get(`/accounts/get_client_ip`)
}

export function approvePunchSet(accountId, punchSetId, punchSetApprovalType = EPunchSetApprovalType.Supervisor) {
    return axiosInstance.post(`/accounts/${accountId}/approve_punchset?punchSetId=${punchSetId}&type=${punchSetApprovalType}`)
}

export function unApprovePunchSet(accountId, punchSetId, punchSetApprovalType = EPunchSetApprovalType.Supervisor) {
    return axiosInstance.post(`/accounts/${accountId}/unapprove_punchset?punchSetId=${punchSetId}&type=${punchSetApprovalType}`)
}

export function getAccountPunchSetExceptions(accountId: string, groupBySupervisor: boolean, referenceDate: string, isPayrollPage: boolean, hierarchyItemId: string, page: number, limit: number, filterByDirectReports: boolean, filterByPunchHierarchy: boolean, filterByDefaultHierarchy: boolean, viewPayrollByJob: boolean, filterObj: IPunchExceptionFilterDto) {
    return axiosInstance.post(`/accounts/${accountId}/exceptions?groupBySupervisor=${groupBySupervisor}&referenceDate=${encodeURIComponent(referenceDate)}&isPayrollPage=${isPayrollPage}&hierarchyItemId=${hierarchyItemId}&page=${page}&limit=${limit}&filterByDirectReports=${filterByDirectReports}&filterByPunchHierarchy=${filterByPunchHierarchy}&filterByDefaultHierarchy=${filterByDefaultHierarchy}&viewPayrollByJob=${viewPayrollByJob}`, filterObj)
}

export function getHierarchyLevelDevices(accountId, levelId, page, limit, search) {
    levelId = (levelId && levelId.length && levelId) || ""
    return axiosInstance.get(`/accounts/${accountId}/hierarchylevel_devices?levelId=${levelId}&page=${page}&limit=${limit}&search=${search}`)
}

export function getAccountUsers(accountId) {
    return axiosInstance.get("/accounts/" + accountId + "/account_users")
}

export function getAllSupervisors(accountId) {
    return axiosInstance.get("/accounts/" + accountId + "/supervisors")
}

export function getSupervisors(accountId, supervisorIds: string[]) {
    return axiosInstance.post("/accounts/" + accountId + "/supervisors", supervisorIds)
}

export function getSupervisorSearch(accountId, filterSupervisorDTO: IFilterSupervisorDTO, search: string, offset: number, limit: number) {
    return axiosInstance.post(`/accounts/${accountId}/filter_supervisors?search=${search}&page=${offset / limit}&limit=${limit}`, filterSupervisorDTO)
}

export function addAccount(account) {
    return axiosInstance.post("/account_registration", account)
}
export function accountRegistration(accountRegistrationDTO: IAccountRegistraionDTO) {
    return axiosInstance.post("/accounts/account_registration", accountRegistrationDTO)
}

export function addAccountUser(accountId, user) {
    return axiosInstance.post("/accounts/" + accountId + "/account_users", user)
}

export function addHierarchyLevelDevice(accountId, levelId, device) {
    levelId = levelId && levelId.length > 0 ? levelId : ""
    return axiosInstance.post("/accounts/" + accountId + "/hierarchylevel_devices?levelId=" + levelId, device)
}

export function addUserToAccount(accountId, user) {
    return axiosInstance.post("/accounts/" + accountId + "/users", user)
}

export function getRecords(
    accountId: string,
    page: number,
    limit: number,
    startDateFilter: Date | string,
    endDateFilter: Date | string,
    levelIdFilter: string = "",
    getAllChildHierarchyData: boolean = true,
    recordSearchFilter: ERecordSearchFilter = ERecordSearchFilter.All
) {
    return accountId && axiosInstance.get(
        "/accounts/" + accountId +
        "/records?" +
        "&page=" + page +
        "&limit=" + limit +
        "&startDateFilter=" + utils.escapeDateTime(startDateFilter) +
        "&endDateFilter=" + utils.escapeDateTime(endDateFilter) +
        "&levelIdFilter=" + levelIdFilter +
        "&recordSearchFilter=" + recordSearchFilter +
        "&getAllChildHierarchyData=" + getAllChildHierarchyData,
        {
            transformResponse: data => JSON.parse(data)
        }
    )
}

export function clockPinExists(accountId, clockPin) {
    return axiosInstance.get("/accounts/" + accountId + "/clockPinExists?clockPin=" + clockPin)
}

export function externalIdExists(accountId, externalId) {
    return axiosInstance.get("/accounts/" + accountId + "/employeeIdExists?employeeId=" + externalId)
}

export function equipmentExternalIdExists(accountId, externalId) {
    return axiosInstance.get("/accounts/" + accountId + "/equipmentExternalIdExists?equipmentId=" + externalId)
}

export function patchAccount(accountId, account) {
    return axiosInstance.patch(`/accounts/${accountId}`, account)
}

export function patchAccountBillingContact(accountId, account) {
    return axiosInstance.patch(`/accounts/${accountId}/billing_contact`, account)
}

export function patchAccountHierarchySettings(accountId, accountHierarchySettings, asyncSessionId) {
    return axiosInstance.patch(`/accounts/${accountId}/accounthierarchysettings?asyncSessionId=${asyncSessionId}`, accountHierarchySettings)
}

export function updateAccountHierarchyDefinitions(accountId, hierarchyDefinitionsList) {
    return axiosInstance.put(`/accounts/${accountId}/hierarchy_definitions`, hierarchyDefinitionsList)
}

export function addHierarchyItem(accountId, hierarchyItem) {
    return axiosInstance.post(`/accounts/${accountId}/hierarchy_items`, hierarchyItem)
}

export function updateAccountHierarchyItem(accountId, hierarchyItem, asyncSessionId) {
    return axiosInstance.put(`/accounts/${accountId}/hierarchy_items/${hierarchyItem.Id}?asyncSessionId=${asyncSessionId}`, hierarchyItem)
}

export function addPositionDefinition(accountId, position) {
    return axiosInstance.post(`/accounts/${accountId}/position_definitions`, position)
}

export function updatePositionDefinition(accountId, position) {
    return axiosInstance.put(`/accounts/${accountId}/position_definitions`, position)
}

export function getMicroLocationByActivationCode(accountId, activationCode) {
    return axiosInstance.get("/accounts/" + accountId + "/microlocations?activationCode=" + activationCode)
}

export function getPositions(employeeId, hierarchyId, shiftId, jobId, phaseId) {
    return axiosInstance.get(`/accounts/get_positions?shiftId=${shiftId ? shiftId : ""}&jobId=${jobId ? jobId : ""}&userId=${employeeId}&hierarchyId=${hierarchyId}&phaseId=${phaseId ? phaseId : ""}`)
}

export function getNewEmployeePositions(employeePositions: IPosition[], hierarchyId, jobId, phaseId) {
    return axiosInstance.post(`/accounts/get_new_employee_positions?jobId=${jobId ? jobId : ""}&hierarchyId=${hierarchyId}&phaseId=${phaseId ? phaseId : ""}`, employeePositions)
}

export function deletePositions(accountId, positionIds) {
    return axiosInstance.post(`/accounts/${accountId}/positions/delete`, positionIds)
}

export function deleteHierarchyItems(accountId, hierarchyIds) {
    return axiosInstance.post(`/accounts/${accountId}/hierarchy_items/delete`, hierarchyIds)
}

export function addHierarchyPosition(hierarchyItemId, position) {
    return axiosInstance.post(`/accounts/hierarchy_position?hierarchyItemId=${hierarchyItemId}`, position)
}

export function updateHierarchyPosition(hierarchyItemId, position) {
    return axiosInstance.put(`/accounts/hierarchy_position?hierarchyItemId=${hierarchyItemId}`, position)
}

export function removeHierarchyPositions(hierarchyItemId, positionIds) {
    return axiosInstance.delete(`/accounts/hierarchy_position?hierarchyItemId=${hierarchyItemId}`, { data: positionIds })
}

export function addPayType(accountId, payType) {
    return axiosInstance.post(`/accounts/${accountId}/pay_types`, payType)
}

export function updatePayType(accountId, payType) {
    return axiosInstance.put(`/accounts/${accountId}/pay_types`, payType)
}

export function deleteValidationPayType(accountId, payTypeId) {
    return axiosInstance.get("/accounts/" + accountId + "/pay_types/" + payTypeId + "/delete_validation")
}

export function deletePayType(accountId, payTypeId) {
    return axiosInstance.delete(`/accounts/${accountId}/pay_types/${payTypeId}`)
}

export function addOTPolicy(accountId, otPolicy) {
    return axiosInstance.post(`/accounts/${accountId}/ot_policies`, otPolicy)
}

export function updateOTPolicy(accountId, otPolicy) {
    return axiosInstance.put(`/accounts/${accountId}/ot_policies`, otPolicy)
}

export function deleteOTPolicy(accountId, otPolicyId) {
    return axiosInstance.delete(`/accounts/${accountId}/ot_policies?otPolicyId=${otPolicyId}`)
}

export function addPayGroup(accountId, payGroup) {
    return axiosInstance.post(`/accounts/${accountId}/pay_groups`, payGroup)
}

export function updatePayGroup(accountId, payGroup, asyncSessionId) {
    return axiosInstance.put(`/accounts/${accountId}/pay_groups?asyncSessionId=${asyncSessionId}`, payGroup)
}

export function deleteValidationPayGroup(accountId, payGroupId) {
    return axiosInstance.get("/accounts/" + accountId + "/pay_groups/" + payGroupId + "/delete_validation")
}

export function deletePayGroup(accountId, payGroupId) {
    return axiosInstance.delete(`/accounts/${accountId}/pay_groups/${payGroupId}`)
}

export function getTimecards(
    accountId: string,
    page: number,
    limit: number,
    payGroupIds: string[],
    levelIdFilter: string = "",
    referenceDate: string = "",
    filterByDirectReports: boolean = false,
    filterByPunchHierarchy: boolean = false,
    filterByDefaultHierarchy: boolean = false,
    filterByException: boolean = false,
    filterExceptions: EExceptionType[] = [],
    filterByTimecardValidationMessages: boolean = false,
    userFilter: IUserFilter = null
) {
    const limitOnAPI = limit === -1 ? null : limit
    return axiosInstance.post(
        "/accounts/" +
        accountId +
        "/get_filtered_timecards?" +
        "levelIdFilter=" +
        levelIdFilter +
        "&page=" +
        page +
        "&limit=" +
        limitOnAPI +
        "&referenceDate=" +
        encodeURIComponent(referenceDate) +
        "&filterByDirectReports=" +
        filterByDirectReports +
        "&filterByPunchHierarchy=" +
        filterByPunchHierarchy +
        "&filterByDefaultHierarchy=" +
        filterByDefaultHierarchy +
        "&filterByException=" +
        filterByException
        + "&filterByTimecardValidationMessages=" + filterByTimecardValidationMessages,
        { payGroupIds, filterExceptions, userFilter }
    )
}

export function getPayGroupsForHierarchyItemAndChildren(accountId, hierarchyItemId) {
    return axiosInstance.get("/accounts/" + accountId + "/pay_groups?hierarchyItemId=" + hierarchyItemId)
}

export function getEquipmentList(accountId, hierarchyItemId, page, limit, search) {
    return axiosInstance.get("/accounts/" + accountId + "/equipment?hierarchyItemId=" + hierarchyItemId + "&page=" + page + "&limit=" + limit + "&search=" + search)
}

export function getTopLevelDevices(accountId, levelIdFilter, page, limit, search) {
    return axiosInstance.get("/accounts/" + accountId + "/topleveldevices?levelIdFilter=" + levelIdFilter + "&page=" + page + "&limit=" + limit + "&search=" + search)
}

export function getPayrollTimeData(accountId, levelIdFilter, payGroupIds: string[], referenceDate, asyncSessionId, payrollByJob) {
    return axiosInstance.post(
        "/accounts/" +
        accountId +
        "/payroll_time_v1?levelIdFilter=" + levelIdFilter +
        "&referenceDate=" + encodeURIComponent(referenceDate) +
        "&asyncSessionId=" + asyncSessionId +
        "&payrollByJob=" + payrollByJob,
        payGroupIds
    )
}

export function asyncGetPayrollTimeData(accountId, levelIdFilter, payGroupIds: string[], referenceDate, asyncSessionId, payrollByJob) {
    return axiosInstance.post(
        "/accounts/" +
        accountId +
        "/async_payroll_time_v1?levelIdFilter=" +
        levelIdFilter +
        "&referenceDate=" +
        encodeURIComponent(referenceDate) +
        "&asyncSessionId=" + asyncSessionId +
        "&payrollByJob=" + payrollByJob,
        payGroupIds
    )
}

export function getPayrollExpensesData(accountId, levelIdFilter, payGroupIds: string[], referenceDate) {
    return axiosInstance.post(
        "/accounts/" +
        accountId +
        "/payroll_expenses_v1?levelIdFilter=" +
        levelIdFilter +
        "&referenceDate=" +
        encodeURIComponent(referenceDate),
        payGroupIds
    )
}
export function getPayrollPriorPeriodExpensesData(accountId, levelIdFilter, payGroupIds: string[], referenceDate) {
    return axiosInstance.post(
        "/accounts/" +
        accountId +
        "/payroll_prior_period_expenses_v1?levelIdFilter=" +
        levelIdFilter +
        "&referenceDate=" +
        encodeURIComponent(referenceDate),
        payGroupIds
    )
}


export function updatePayrollDefinitionLevel(accountId, payrollDefinitionLevelId, asyncSessionId) {
    const payload = payrollDefinitionLevelId ? payrollDefinitionLevelId : ""
    return axiosInstance.get(`/accounts/${accountId}/update_payroll_definition_level?payrollDefinitionLevelId=${payload}&asyncSessionId=${asyncSessionId}`)
}

export function approveTimecards(accountId, objectTimecardIds, jobId, dayOfWeekIndex, punchSetApprovalType = EPunchSetApprovalType.Supervisor) {
    return axiosInstance.post(`/accounts/${accountId}/timecards/approve?jobId=${jobId}&dayOfWeekIndex=${dayOfWeekIndex}&type=${punchSetApprovalType}`, objectTimecardIds)
}

export function approveAllTimecards(accountId, referenceDate, hierarchyItemId, filterByDirectReports: boolean, filterByDefaultHierarchy: boolean, filterByException: boolean, payGroupIds: string[], filterExceptions: EExceptionType[], asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/timecards/approve_all_v1?referenceDate=${encodeURIComponent(referenceDate)}&hierarchyItemId=${hierarchyItemId}&filterByDirectReports=${filterByDirectReports}&filterByDefaultHierarchy=${filterByDefaultHierarchy}&filterByException=${filterByException}&asyncSessionId=${asyncSessionId}`,
        { payGroupIds, filterExceptions }
    )
}

export function unapproveAllTimecards(accountId, referenceDate, hierarchyItemId, filterByDirectReports: boolean, filterByDefaultHierarchy: boolean, filterByException: boolean, payGroupIds: string[], filterExceptions: EExceptionType[], asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/timecards/unapprove_all_v1?referenceDate=${encodeURIComponent(referenceDate)}&hierarchyItemId=${hierarchyItemId}&filterByDirectReports=${filterByDirectReports}&filterByDefaultHierarchy=${filterByDefaultHierarchy}&filterByException=${filterByException}&asyncSessionId=${asyncSessionId}`,
        { payGroupIds, filterExceptions }
    )
}

export function unapproveTimecards(accountId, objectTimecardIds, jobId, dayOfWeekIndex, punchSetApprovalType = EPunchSetApprovalType.Supervisor) {
    return axiosInstance.post(`/accounts/${accountId}/timecards/unapprove?jobId=${jobId}&dayOfWeekIndex=${dayOfWeekIndex}&type=${punchSetApprovalType}`, objectTimecardIds)
}

export function asyncLockPayrollTimeHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], individualLockEmployeeIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/async_lock_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { payrollPayGroupDataList, payGroupIds, individualLockEmployeeIds }
    )
}

export function asyncEmployeeLockPayrollTimeHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/async_employee_lock_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { payrollPayGroupDataList, payGroupIds }
    )
}

export function lockPayrollTimeHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], individualLockEmployeeIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/lock_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { payrollPayGroupDataList, payGroupIds, individualLockEmployeeIds }
    )
}

export function employeeLockPayrollTimeHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/employee_lock_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { payrollPayGroupDataList, payGroupIds }
    )
}

export function asyncUnlockPayrollTimeHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], individualLockEmployeeIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/async_unlock_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { payrollPayGroupDataList, payGroupIds, individualLockEmployeeIds }
    )
}

export function asyncEmployeeUnlockPayrollTimeHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/async_employee_unlock_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { payrollPayGroupDataList, payGroupIds }
    )
}

export function unlockPayrollTimeHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], individualLockEmployeeIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/unlock_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { payrollPayGroupDataList, payGroupIds, individualLockEmployeeIds }
    )
}

export function employeeUnlockPayrollTimeHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/employee_unlock_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { payrollPayGroupDataList, payGroupIds }
    )
}

export function lockPayrollExpensesHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], individualLockEmployeeIds: string[], startDate, endDate, levelIdFilter) {
    return axiosInstance.post(
        `/accounts/${accountId}/lock_payroll_expenses_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}`,
        { payrollPayGroupDataList, payGroupIds, individualLockEmployeeIds }
    )
}

export function employeeLockPayrollExpensesHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter) {
    return axiosInstance.post(
        `/accounts/${accountId}/employee_lock_payroll_expenses_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}`,
        { payrollPayGroupDataList, payGroupIds }
    )
}

export function asyncExportEmployeePayrollTime(accountId, employeeIds, payGroupIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/async_export_payroll_time_v1?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(
            endDate
        )}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { employeeIds, payGroupIds }
    )
}

export function exportEmployeePayrollTime(accountId, employeeIds, payGroupIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/export_payroll_time_v1?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(
            endDate
        )}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { employeeIds, payGroupIds }
    )
}

export function exportEmployeePayrollExpenses(accountId, employeeIds, payGroupIds: string[], startDate, endDate, levelIdFilter) {
    return axiosInstance.post(
        `/accounts/${accountId}/export_payroll_expenses_v1?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(
            endDate
        )}&levelIdFilter=${levelIdFilter}`,
        { employeeIds, payGroupIds }
    )
}

export function expensePayrollFileDownload(accountId: string, expenseId: string) {
    return axiosInstance.get(`/accounts/${accountId}/download_expense_payroll_file_v1?expenseId=${encodeURIComponent(expenseId)}`)
}

export function exportEmployeePayrollPriorPeriodExpenses(accountId, employeeIds, payGroupIds: string[], startDate, endDate, levelIdFilter) {
    return axiosInstance.post(
        `/accounts/${accountId}/export_payroll_prior_period_expenses_v1?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(
            endDate
        )}&levelIdFilter=${levelIdFilter}`,
        { employeeIds, payGroupIds }
    )
}

export function checkCanChangePaygroupCalendarSettings(accountId, payGroupId) {
    return axiosInstance.get(`/accounts/${accountId}/pay_groups/${payGroupId}/can_change_calendar_settings`)
}

export function checkCanChangeHierarchyPayGroup(accountId, hierarchyId, newPayGroupId) {
    return axiosInstance.get(`/accounts/${accountId}/can_change_paygroup?hierarchyItemId=${hierarchyId}&newPaygroupId=${newPayGroupId}`)
}

export function checkCanChangeJobPayGroup(accountId, jobId, newPayGroupId) {
    return axiosInstance.get(`/accounts/${accountId}/can_change_job_paygroup?jobId=${jobId}&newPaygroupId=${newPayGroupId}`)
}

export function checkCanDeleteHierarchy(accountId, hierarchyId) {
    return axiosInstance.get(`/accounts/${accountId}/hierarchy_items/${hierarchyId}/can_delete`)
}

export function getEmployeeTimecardPayrollStatus(accountId, employeeId, timecardStartDate, timecardEndDate, payrollType) {
    return axiosInstance.get(
        `/accounts/${accountId}/payroll_status?employeeId=${employeeId}&startDate=${encodeURIComponent(timecardStartDate)}&endDate=${encodeURIComponent(timecardEndDate)}&payrollType=${payrollType}`
    )
}

export function unlockPayrollExpensesHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], individualLockEmployeeIds: string[], startDate, endDate, levelIdFilter) {
    return axiosInstance.post(
        `/accounts/${accountId}/unlock_payroll_expenses_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}`,
        { payrollPayGroupDataList, payGroupIds, individualLockEmployeeIds }
    )
}

export function employeeUnlockPayrollExpensesHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter) {
    return axiosInstance.post(
        `/accounts/${accountId}/employee_unlock_payroll_expenses_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}`,
        { payrollPayGroupDataList, payGroupIds }
    )
}

export function addAccrual(accountId, accrual) {
    return axiosInstance.post(`/accounts/${accountId}/accruals`, accrual)
}

export function updateAccrual(accountId, accrual) {
    return axiosInstance.put(`/accounts/${accountId}/accruals`, accrual)
}

export function deleteAccrual(accountId, accrualId) {
    return axiosInstance.delete(`/accounts/${accountId}/accruals/${accrualId}`)
}

export function getLeaveRequests(accountId, timeoffFilter: ITimeoffFilter, page, limit) {
    return axiosInstance.post(`/accounts/${accountId}/timeoff_v1?page=${page}&limit=${limit}`, timeoffFilter)
}

export async function getTimezoneList() {
    const res = await axiosInstance.get(`/accounts/timezonelist`)
    return res.data
}

export function asyncClosePayrollTimeHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/async_close_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { payrollPayGroupDataList, payGroupIds }
    )
}

export function closePayrollTimeHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/close_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { payrollPayGroupDataList, payGroupIds }
    )
}

export function asyncLockClosePayrollTimeHierarchy(accountId, lockPayrollPayGroupDataList: IPayrollPayGroupData[], closePayrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/async_lock_close_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { lockPayrollPayGroupDataList, closePayrollPayGroupDataList, payGroupIds }
    )
}

export function lockClosePayrollTimeHierarchy(accountId, lockPayrollPayGroupDataList: IPayrollPayGroupData[], closePayrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter, asyncSessionId) {
    return axiosInstance.post(
        `/accounts/${accountId}/lock_close_payroll_time_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}&asyncSessionId=${asyncSessionId}`,
        { lockPayrollPayGroupDataList, closePayrollPayGroupDataList, payGroupIds }
    )
}

export function closePayrollExpensesHierarchy(accountId, payrollPayGroupDataList: IPayrollPayGroupData[], payGroupIds: string[], startDate, endDate, levelIdFilter) {
    return axiosInstance.post(
        `/accounts/${accountId}/close_payroll_expenses_v2?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&levelIdFilter=${levelIdFilter}`,
        { payrollPayGroupDataList, payGroupIds }
    )
}

export function addShiftTemplate(accountId, shiftTemplate) {
    return axiosInstance.post(`/accounts/${accountId}/shifttemplates`, shiftTemplate)
}

export function updateShiftTemplate(accountId, shiftTemplate) {
    return axiosInstance.put(`/accounts/${accountId}/shifttemplates`, shiftTemplate)
}

export function deleteShiftTemplates(accountId, shiftTemplateIds) {
    return axiosInstance.post(`/accounts/${accountId}/shifttemplates/delete`, shiftTemplateIds)
}

export function addPatternTemplate(accountId, patternTemplate) {
    return axiosInstance.post(`/accounts/${accountId}/patterntemplates`, patternTemplate)
}

export function updatePatternTemplate(accountId, patternTemplate) {
    return axiosInstance.put(`/accounts/${accountId}/patterntemplates`, patternTemplate)
}

export function deletePatternTemplates(accountId, patternTemplateIds) {
    return axiosInstance.delete(`/accounts/${accountId}/patterntemplates`, patternTemplateIds)
}

export function addScheduleTemplate(accountId, scheduleTemplate: IScheduleTemplate) {
    return axiosInstance.post(`/accounts/${accountId}/scheduletemplates`, scheduleTemplate)
}

export function addScheduleTemplates(accountId, scheduleTemplates: IScheduleTemplate[]) {
    return axiosInstance.post(`/accounts/${accountId}/scheduletemplates_list`, scheduleTemplates)
}

export function updateScheduleTemplate(accountId, scheduleTemplate) {
    return axiosInstance.put(`/accounts/${accountId}/scheduletemplates`, scheduleTemplate)
}

export function deleteScheduleTemplates(accountId, scheduleTemplateIds) {
    return axiosInstance.post(`/accounts/${accountId}/scheduletemplates/delete`, scheduleTemplateIds)
}

export function getAllowChangeAccAccrual(accountId, accrualId) {
    return axiosInstance.get(`/accounts/${accountId}/getallowchangeaccaccrual?accrualId=${accrualId}`)
}

export function addHolidayTemplate(accountId, holidayTemplate) {
    return axiosInstance.post(`/accounts/${accountId}/holidayTemplates`, holidayTemplate)
}

export function updateHolidayTemplate(accountId, holidayTemplate, asyncSessionId) {
    return axiosInstance.put(`/accounts/${accountId}/holidayTemplates?asyncSessionId=${asyncSessionId}`, holidayTemplate)
}

export function deleteHolidayTemplate(accountId, holidayTemplateId) {
    return axiosInstance.delete(`/accounts/${accountId}/holidayTemplates/${holidayTemplateId}`)
}

export function checkValidateScheduleName(accountId, scheduleHierarchyId, scheduleJobId, startDate, endDate, scheduleName, masterSchedule) {
    return axiosInstance.get(`/accounts/${accountId}/validate_schedule_name?scheduleHierarchyId=${scheduleHierarchyId}&scheduleJobId=${scheduleJobId}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&scheduleName=${scheduleName}&masterSchedule=${masterSchedule}`)
}

export function checkValidateScheduleDates(accountId, scheduleJobId, startDate, endDate, masterSchedule) {
    return axiosInstance.get(`/accounts/${accountId}/validate_schedule_dates?scheduleJobId=${scheduleJobId}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&masterSchedule=${masterSchedule}`)
}

export function checkCanUnlinkPayTypeFromPayGroup(accountId, payGroupId, payTypeId) {
    return axiosInstance.get(`/accounts/${accountId}/pay_groups/${payGroupId}/delete_linkedpaytype_validation?payTypeId=${payTypeId}`)
}

export function CheckValidateAccountAccrualPayTypes(accountId, accrualId, payTypeIds) {
    return axiosInstance.post(`/accounts/${accountId}/Accrual/${accrualId}/ValidatePayType`, payTypeIds)
}

export function getJobs(accountId, hierarchyItemId, page, limit, search, active, filterByDirectReports, restrictHierarchy, punchEmployeeId, clientId: string, jobStatusId = "", jobSubStatusId = "", filterOnlyFavorites = false, jobType?) {
    return axiosInstance.get(
        "/accounts/" + accountId + "/jobs?" +
        "hierarchyItemId=" + hierarchyItemId +
        "&page=" + page +
        "&limit=" + limit +
        "&search=" + search +
        "&active=" + active +
        "&filterByDirectReports=" + filterByDirectReports +
        "&restrictHierarchy=" + restrictHierarchy +
        "&jobstatusId=" + jobStatusId +
        "&jobSubStatusId=" + jobSubStatusId +
        "&jobType=" + jobType +
        "&filterOnlyFavorites=" + filterOnlyFavorites +
        "&punchEmployeeId=" + punchEmployeeId +
        "&clientId=" + clientId
    )
}

export function searchJobs(accountId, page, limit, jobFilter: IJobFilter) {
    return axiosInstance.post(`/accounts/${accountId}/search_jobs?page=${page}&limit=${limit}`, jobFilter)
}

export function exportToThirdParty(startDate: string = null, endDate: string = null) {
    return axiosInstance.post(`/accounts/export_third_party?startDate=${startDate}&endDate=${endDate}`)
}

export function getPreQuestions(accountId, guid = "") {
    return axiosInstance.get(`/accounts/${accountId}/pre_questions?guid=${guid}`)
}

export function updateAccrualSettings(accountId: string, settings: IAccrualSettings) {
    return axiosInstance.post(`/accounts/${accountId}/update_accrual_settings`, settings)
}

export function addOrUpdateAccrualAdjustmentReason(accountId: string, adjustment: IAccrualAdjustment) {
    return axiosInstance.post(`/accounts/${accountId}/accrual_adjustment`, adjustment)
}

export function deleteAccrualAdjustmentReason(accountId: string, adjustmentIds: string[]) {
    return axiosInstance.delete(`/accounts/${accountId}/accrual_adjustment`, { data: adjustmentIds })
}

export function searchUsers(accountId, page, limit, userFilter: IUserFilter) {
    return axiosInstance.post(`/accounts/${accountId}/search_users?page=${page}&limit=${limit}`, userFilter)
}

export function updateAccountPlanType(accountId: string, planType: EPlanType, enableSchedule: boolean, enableTimeoff: boolean) {
    return axiosInstance.post(`/accounts/${accountId}/plan_type`, { PlanType: planType, EnableSchedule: enableSchedule, EnableTimeoff: enableTimeoff })
}

export function updateAccountScheduleFeature(accountId: string, featureOn: boolean) {
    return axiosInstance.post(`/accounts/${accountId}/schedule_feature`, { featureOn: featureOn })
}

export function updateAccountTimeoffFeature(accountId: string, featureOn: boolean) {
    return axiosInstance.post(`/accounts/${accountId}/timeoff_feature`, { featureOn: featureOn })
}

export function checkValidateHierarchyCostCode(accountId: string, hierarchyId: string, costCode: string) {
    return axiosInstance.get(`/accounts/${accountId}/validate_hierarchy_cost_code?hierarchyId=${hierarchyId}&costCode=${costCode}`)
}

export function getFrontEndSettings() {
    return axiosInstance.get(`/account_manager/fe_settings`)
}

export function getFreeAccountMetricsData(accountId) {
    return axiosInstance.get(`/accounts/${accountId}/free_account_metrics_data`)
}

export function getActiveJobCount() {
    return axiosInstance.get(`/accounts/active_job_count`)
}

export function disableAssignJobPunch(accountId) {
    return axiosInstance.get(`/accounts/${accountId}/disable_assign_job_punch`)
}

export function recalcRecacheTimecards(accountId: string, recalc: boolean, referenceDate: string, allTimecards: boolean, excludeInactiveEmployees: boolean, payrollHierarchyIds: string[], asyncSessionId: string) {
    return axiosInstance.post(`/accounts/${accountId}/recalc_recache_timecards`,
        { recalc, recache: !recalc, referenceDate, allTimecards, excludeInactiveEmployees, payrollHierarchyIds, asyncSessionId })
}

export function getJobVariations(accountId, hierarchyId, settingPropertyName) {
    return axiosInstance.get(`/accounts/${accountId}/get_job_variations?hierarchyId=${hierarchyId}&settingPropertyName=${settingPropertyName}`)
}